import clsx from "clsx";
import { Radio, RadioGroup, Label } from "@headlessui/react";
import { CheckCircleIcon } from "@phosphor-icons/react";

interface RadioOption {
	id: string | number;
	value: string;
	label?: string;
	icon?: React.ReactNode;
}

interface RadioGroupFieldProps {
	id: string;
	label: string;
	value: string;
	options: RadioOption[];
	onChange: (value: string) => void;
	required?: boolean;
	error?: string;
	disabled?: boolean;
	className?: string;
	gridCols?: number;
	showIcons?: boolean;
	disabledOptions?: string[];
	navRef?: boolean;
}

export function RadioGroupField({
	id,
	label,
	value,
	options,
	onChange,
	error,
	required = false,
	disabled = false,
	className = "",
	gridCols = 2,
	showIcons = false,
	disabledOptions = [],
	navRef = false,
}: RadioGroupFieldProps) {
	const colClass = {
		1: "eF-grid-cols-1",
		2: "eF-grid-cols-2",
		3: "eF-grid-cols-2 md:eF-grid-cols-3",
		4: "eF-grid-cols-2 md:eF-grid-cols-4",
		5: "eF-grid-cols-2 md:eF-grid-cols-5",
		6: "eF-grid-cols-2 md:eF-grid-cols-6",
		7: "eF-grid-cols-2 md:eF-grid-cols-7",
		8: "eF-grid-cols-4 md:eF-grid-cols-8",
	}[gridCols];

	return (
		<div className={className}>
			<RadioGroup
				id={id}
				aria-label={label}
				value={value}
				onChange={onChange}
				disabled={disabled}
				aria-required={required}
				aria-describedby={error ? `${id}-error` : ""}
				aria-invalid={!error}
			>
				<Label className="eF-text-base eF-font-semibold eF-leading-6 eF-text-gray-900">
					{label}
				</Label>
				<div
					style={{ gridAutoRows: "1fr" }}
					className={clsx(
						navRef ? "eF-grid-cols-2" : colClass,
						`eF-grid eF-mt-2
             eF-gap-x-2 eF-gap-y-2 md:eF-gap-x-2`,
					)}
				>
					{options.map((item) => {
						const isOptionDisabled = disabledOptions.includes(item.value);
						const optionId = `${id}-option-${item.id}`;
						return (
							<Radio
								key={item.value}
								value={item.value}
								disabled={isOptionDisabled}
								id={optionId}
								aria-describedby={
									isOptionDisabled ? `${optionId}-disabled` : undefined
								}
								className={({ checked }) =>
									clsx(
										"eF-h-full eF-flex-1 eF-min-h-11",
										disabled || isOptionDisabled
											? "eF-opacity-50 eF-cursor-not-allowed"
											: "eF-cursor-pointer",
										isOptionDisabled ? "eF-bg-gray-50" : "eF-bg-white",
										checked && !error
											? "eF-border-[--eF-theme-color] eF-ring-1 eF-ring-[--eF-theme-color]"
											: "",
										error
											? "eF-border-red-600"
											: !checked
												? "eF-border-gray-300"
												: "",
										showIcons ? "eF-flex-1" : "eF-justify-center",
										"eF-relative eF-flex eF-rounded-lg eF-border eF-p-2 eF-shadow-sm focus:eF-outline-none focus-visible:eF-outline-[--eF-theme-color] md:eF-p-3",
									)
								}
							>
								{({ checked }) => (
									<>
										<span
											className={clsx(
												showIcons ? "eF-flex eF-flex-1" : "eF-flex",
											)}
										>
											<span
												className={clsx(
													navRef ? "" : "",
													showIcons ? "eF-items-start" : "",
													"eF-flex eF-flex-col eF-justify-center",
												)}
											>
												{showIcons && item.icon && (
													<div
														className={clsx(
															!checked
																? " eF-text-gray-900"
																: "eF-text-[--eF-theme-color]",
														)}
													>
														{item.icon}
													</div>
												)}
												<Label
													as="span"
													className={clsx(
														!checked ? "eF-flex eF-text-gray-900" : "",
														"eF-text-wrap eF-text-[--eF-theme-color] eF-flex eF-text-sm eF-leading-4",
													)}
												>
													{item.label || item.value}
												</Label>
											</span>
										</span>
										<CheckCircleIcon
											className={clsx(
												!checked ? "eF-invisible" : "",
												"eF-h-5 eF-w-5 eF-text-[--eF-theme-color] eF-absolute eF-bg-white -eF-right-1 -eF-top-1",
											)}
											aria-hidden="true"
											weight="fill"
										/>
										<span
											className={clsx(
												checked
													? "eF-border-[--eF-theme-color]"
													: "eF-border-transparent",

												"eF-pointer-events-none eF-absolute eF-rounded-lg",
											)}
											aria-hidden="true"
										/>
									</>
								)}
							</Radio>
						);
					})}
				</div>
			</RadioGroup>
			{error && (
				<p
					id={`${id}-error`}
					className="eF-mt-1 eF-text-xs eF-text-red-600"
					role="alert"
					aria-live="polite"
				>
					{error}
				</p>
			)}
		</div>
	);
}
